<template>
  <div>
    <breadcrumbs :home="product.name + ': Move to duty paid'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

            <v-row no-gutters>
              <v-col cols="6" sm="4" lg="3">
                <date-picker v-model="transaction.date" @change="refresh"/>
              </v-col>
            </v-row>

            <cases-bottles :cases.sync="transaction.cases" :bottles.sync="transaction.bottles" :product="product" :availableCount="available" avaiableSuffix="Available"/>

            <v-row>
              <v-col cols="6" sm="3" lg="2" v-if="company.settings.lotTrackingForProducts">
                <v-combobox v-model="transaction.lot" outlined dense label="Lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
              </v-col>
              <v-col cols="12" sm="6" lg="4" v-if="fromSites.length>1">
                <v-autocomplete outlined dense label="From site" v-model="transaction.fromSiteId" :items="fromSites" item-value="id" item-text="name"/>
              </v-col>
              <v-col cols="12" sm="6" lg="4" v-if="toSites.length>1">
                <v-autocomplete outlined dense label="To site" v-model="transaction.toSiteId" :items="toSites" item-value="id" item-text="name"/>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
              </v-col>
            </v-row>
          </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="ml-6 mr-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import CasesBottles from '@/components/CasesBottles'

export default {
  name: 'InventoryProductsMoveToDutyPaid',
  mixins: [confirmLeave],
  components: {CasesBottles},
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      init: true,
      edit: false,
      product: {name:'',sites:[]},
      valid:true,
      saving:false,
      transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    cases:0, bottles: 0, lot:'', comments: '',fromSiteId:'',toSiteId:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    fromSites() {
      return this.$store.getters.sites(!this.transactionId, {B262:true});
    },
    toSites() {
      return this.$store.getters.sites(!this.transactionId);
    },
    currentLots() {
      return this.product.sites.filter(site => site.siteId === this.transaction.fromSiteId && site.inBondBottles)
                                .map(site => site.lot);
    },
    available() {
      let site;
      if (!this.company.settings.lotTrackingForProducts)
        site = this.product.sites.reduce((site, productSite) => {
          if (productSite.siteId === this.transaction.fromSiteId) {
            site.dutyPaidBottles += productSite.dutyPaidBottles;
            site.inBondBottles += productSite.inBondBottles;
          }
          return site;
        }, {dutyPaidBottles:0, inBondBottles:0});
      else
        site = this.product.sites.find(site => site.siteId === this.transaction.fromSiteId && site.lot === this.transaction.lot);
      return site?.inBondBottles || 0;
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    if (this.transactionId) {
      const results = await this.$apiCall(`query InventoryProductsMoveToDutyPaid($id:ID!) {
                                                productMoveToDutyPaid(id:$id) {
                                                  id,productId,date,bottles,cases,lot,comments,fromSiteId,toSiteId
                                                }
                                              }`,
                                          {id:this.transactionId});
      if (results.error) {
        this.$nextTick(() => this.dirty = false);
        this.$router.back();
        return;
      }
      this.transaction = results.data.productMoveToDutyPaid;
    }
    else {
      this.transaction.fromSiteId = this.fromSites[0].id;
      this.transaction.toSiteId = this.toSites[0].id;
      this.edit = true;
    }
    this.init = false;
    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    async refresh() {
      const results = await this.$apiCall(`query InventoryProductsMoveToDutyPaid($id:ID!,$asof:DateTime!) {
                                                product(id:$id) {
                                                  id,name,caseSize,mixedPack,containerType
                                                  sites(asof:$asof) {siteId,lot,inBondBottles}
                                                }
                                              }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date});
      if (!results.error) {
        this.product = results.data.product;
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        this.saving = true;
        const results = await this.$apiCall(`mutation InventoryProductsMoveToDutyPaid($input: ProductMoveToDutyPaidInput!) {
                                                productMoveToDutyPaidCreate(input:$input) {id}
                                              }`,
                                            {input:this.transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} move to duty paid transaction for ${this.product.name}`});
        }
      }
		}
	}
}
</script>